import { restService } from '@/lib/utils/rest-service';

import type {
  Entity,
  EntityFilters,
  EntityList,
} from '@/lib/data/shared/entity';
import { objectToQueryString } from './helpers';

export interface CRUDResponse<T> {
  data?: T;
  status?: number;
  statusText?: string;
  success?: boolean;
  error?: string;
}

export interface CRUDService<T> {
  fetchLookup({
    filters,
    requestInit,
    showErrorToast,
  }: {
    filters?: EntityFilters;
    requestInit?: RequestInit;
    showErrorToast?: boolean;
  }): Promise<CRUDResponse<Array<any>>>;
  fetchList({
    filters,
    requestInit,
    showErrorToast,
  }: {
    filters?: EntityFilters;
    requestInit?: RequestInit;
    showErrorToast?: boolean;
  }): Promise<CRUDResponse<EntityList<T>>>;
  fetchById({
    id,
    requestInit,
    showErrorToast,
  }: {
    id: string;
    requestInit?: RequestInit;
    showErrorToast?: boolean;
  }): Promise<CRUDResponse<T>>;
  upsert({
    item,
    requestInit,
    showErrorToast,
  }: {
    item: Partial<T>;
    requestInit?: RequestInit;
    showErrorToast?: boolean;
  }): Promise<CRUDResponse<T>>;
  remove({
    id,
    requestInit,
    showErrorToast,
  }: {
    id: string;
    requestInit?: RequestInit;
    showErrorToast?: boolean;
  }): Promise<CRUDResponse<boolean>>;
}

export function getRestClient<T extends Entity>(
  entityName: string,
): CRUDService<T> {
  return {
    fetchLookup: async ({ filters, requestInit, showErrorToast }) =>
      restService.request({
        url: `/${entityName}/lookup${
          filters ? `?${objectToQueryString(filters)}` : ''
        }`,
        requestInit,
        showErrorToast,
      }),
    fetchList: async ({ filters, requestInit, showErrorToast }) =>
      restService.request({
        url: `/${entityName}/list${
          filters ? `?${objectToQueryString(filters)}` : ''
        }`,
        requestInit,
        showErrorToast,
      }),
    fetchById: async ({ id, requestInit, showErrorToast }) =>
      restService.request({
        url: `/${entityName}/${id}`,
        requestInit,
        showErrorToast,
      }),
    upsert: async ({ item, requestInit, showErrorToast }) =>
      restService.request({
        url: `/${entityName}${!item.id ? '/create' : `/update/${item.id}`}`,
        method: item.id ? 'PUT' : 'POST',
        body: item,
        requestInit,
        showErrorToast,
      }),
    remove: async ({ id, requestInit, showErrorToast }) =>
      restService.request({
        url: `/${entityName}/${id}/delete`,
        method: 'DELETE',
        requestInit,
        showErrorToast,
      }),
  };
}
